import { z } from 'zod';
import { RelationTypeSchema } from '../enums';

// Base Controller Schema
export const BaseControllerSchema = z.object({
  controllerId: z.string().optional(), // MongoDB ObjectId
  name: z.string().min(1, 'Controller name is required').max(100, 'Name too long'),
  cnic: z.string().length(13, 'CNIC must be exactly 13 digits').regex(/^\d{13}$/, 'CNIC must contain only digits'),
  phoneNo: z.string().min(10, 'Phone number must be at least 10 digits').max(15, 'Phone number too long'),
  fatherName: z.string().min(1, 'Father name is required').max(100, 'Name too long'),
  address: z.string().min(10, 'Address must be at least 10 characters').max(500, 'Address too long'),
  isActive: z.boolean().default(true),
  createdAt: z.date().optional(),
  updatedAt: z.date().optional()
});

export type BaseController = z.infer<typeof BaseControllerSchema>;

// Controller Creation Schema
export const CreateControllerSchema = BaseControllerSchema.omit({
  controllerId: true,
  createdAt: true,
  updatedAt: true
});

export type CreateController = z.infer<typeof CreateControllerSchema>;

// Controller Update Schema
export const UpdateControllerSchema = BaseControllerSchema.partial().extend({
  controllerId: z.string() // Required for updates
});

export type UpdateController = z.infer<typeof UpdateControllerSchema>;

// Controller Response Schema (for API responses)
export const ControllerResponseSchema = BaseControllerSchema.extend({
  controllerId: z.string()
});

export type ControllerResponse = z.infer<typeof ControllerResponseSchema>;

// Controller with Relations Schema
export const ControllerWithRelationsSchema = ControllerResponseSchema.extend({
  assignedRegions: z.array(z.object({
    regionId: z.number(),
    regionName: z.string()
  })).optional(),
  assignedOffices: z.array(z.object({
    officeNo: z.number(),
    officeName: z.string()
  })).optional()
});

export type ControllerWithRelations = z.infer<typeof ControllerWithRelationsSchema>;

// Controller List Query Schema
export const ControllerListQuerySchema = z.object({
  page: z.number().int().min(1).default(1),
  limit: z.number().int().min(1).max(100).default(20),
  search: z.string().optional(),
  isActive: z.boolean().optional(),
  hasUnassignedOffices: z.boolean().optional(),
  sortBy: z.enum(['name', 'cnic', 'createdAt']).default('name'),
  sortOrder: z.enum(['asc', 'desc']).default('asc')
});

export type ControllerListQuery = z.infer<typeof ControllerListQuerySchema>;

// Controller Assignment Schema
export const ControllerAssignmentSchema = z.object({
  controllerId: z.string(),
  entityType: z.enum(['region', 'office']),
  entityId: z.union([z.number(), z.string()]),
  action: z.enum(['assign', 'unassign'])
});

export type ControllerAssignment = z.infer<typeof ControllerAssignmentSchema>;

// Controller Statistics Schema
export const ControllerStatisticsSchema = z.object({
  controllerId: z.string(),
  controllerName: z.string(),
  totalAssignedRegions: z.number(),
  totalAssignedOffices: z.number(),
  totalDonationsUnderControl: z.number(),
  totalAmountUnderControl: z.number(),
  period: z.object({
    start: z.date(),
    end: z.date()
  })
});

export type ControllerStatistics = z.infer<typeof ControllerStatisticsSchema>; 